import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  DEFAULT_MAXIMUM_MESSAGE_LENGTH,
  EMAIL_SUBJECT_MAXIMUM_LENGTH,
  SMS_MAXIMUM_LENGTH,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/ChannelMessageInput';
import { NotificationType } from 'hooks/useNotification';
import type { EventNotificationFormValues } from './eventNotification';

type MessageFieldName =
  | 'emailMessage'
  | 'smsMessage'
  | 'appMessage'
  | 'teamsMessage';

const MESSAGE_FIELDS: Partial<
  Record<
    NotificationType,
    { name: MessageFieldName; label: string; maxLength: number }
  >
> = {
  [NotificationType.EMAIL]: {
    name: 'emailMessage',
    label: 'Email Message',
    maxLength: DEFAULT_MAXIMUM_MESSAGE_LENGTH,
  },
  [NotificationType.IN_APP]: {
    name: 'appMessage',
    label: 'In-App Message',
    maxLength: DEFAULT_MAXIMUM_MESSAGE_LENGTH,
  },
  [NotificationType.TEAMS]: {
    name: 'teamsMessage',
    label: 'Teams Message',
    maxLength: DEFAULT_MAXIMUM_MESSAGE_LENGTH,
  },
  [NotificationType.SMS]: {
    name: 'smsMessage',
    label: 'SMS Message',
    maxLength: SMS_MAXIMUM_LENGTH,
  },
};

// Duplicates NotificationsTab/ChannelMessageInput, rebuilt with shadcn.
// TODO: Replace both with one shared component when the existing
// notifications UI is migrated to shadcn.
const EventChannelMessageInput: React.FC = () => {
  const {
    register,
    watch,
    formState: { errors },
  } = useFormContext<EventNotificationFormValues>();
  const [activeChannel, setActiveChannel] = useState<NotificationType | null>(
    null
  );
  const selectedChannels = watch('notificationTypes') || [];

  const displayChannel =
    activeChannel && selectedChannels.includes(activeChannel)
      ? activeChannel
      : selectedChannels[0] || null;

  if (!displayChannel) return null;

  const messageField = MESSAGE_FIELDS[displayChannel];
  const messageError =
    errors.emailSubject ??
    errors.emailMessage ??
    errors.appMessage ??
    errors.teamsMessage ??
    errors.smsMessage;

  return (
    <div className="tw:grid tw:gap-4">
      {selectedChannels.length > 1 && (
        <ToggleGroup
          variant="outline"
          value={[displayChannel]}
          onValueChange={(values) => {
            if (values[0]) setActiveChannel(values[0] as NotificationType);
          }}
        >
          {selectedChannels.map((channel) => (
            <ToggleGroupItem key={channel} value={channel}>
              {channel.toUpperCase()}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      )}
      {displayChannel === NotificationType.EMAIL && (
        <Field data-invalid={!!errors.emailSubject}>
          <FieldLabel htmlFor="eventNotificationEmailSubject">
            Email Subject
          </FieldLabel>
          <Input
            id="eventNotificationEmailSubject"
            aria-invalid={!!errors.emailSubject}
            maxLength={EMAIL_SUBJECT_MAXIMUM_LENGTH}
            {...register('emailSubject')}
          />
        </Field>
      )}
      {messageField && (
        <Field
          key={messageField.name}
          data-invalid={!!errors[messageField.name]}
        >
          <FieldLabel htmlFor={`eventNotification-${messageField.name}`}>
            {messageField.label}
          </FieldLabel>
          <Textarea
            id={`eventNotification-${messageField.name}`}
            rows={4}
            aria-invalid={!!errors[messageField.name]}
            maxLength={messageField.maxLength}
            {...register(messageField.name)}
          />
        </Field>
      )}
      <FieldError errors={[messageError]} />
    </div>
  );
};

export default EventChannelMessageInput;
